import React, { useEffect } from 'react';
import cx from 'classnames';
import { getFlyingBetslipConfig } from 'helpers/prematch';
import PropTypes from 'prop-types';

import {
  BetslipProvider,
  Bus,
  DetectionProvider,
  Fuse,
  GlobalBetslipProvider,
  printMissingConfigError,
  SiteConfigManager,
  useApplicationState,
} from '@atlas/core';

import { getLocalBetslipState } from 'common/Betslip/LocalBetslipContext';
import CustomStylesPortal from 'components/CustomStylesPortal/CustomStylesPortal';
import { useActionOnNavigation } from 'components/Layout/useActionOnNavigation';
import useThemeBootstrap from 'components/Layout/useThemeBootstrap';
import addBetBoosterWindowFunctions from 'components/Pages/Details/BetBooster/addBetBoosterWindowFunctions';
import { useSearchInputState } from 'components/Search/context/InputProvider';
import { useGetUnreadMessages } from 'hooks/useGetUnreadMessages';
import { useShowMessagesPopup } from 'hooks/useShowMessagesPopup';
import { COOKIES_ACCEPTED_EVENT } from 'src/common/constants';
import { SmarticoProvider } from 'src/components/ClientProfile/Achievements/Smartico';
import useRedirectAfterDeposit from 'src/hooks/useRedirectAfterDeposit';

import Footer from '../Footer/Footer';

import DefaultLayout from './DefaultLayout';
import LayoutErrorFallback from './LayoutErrorFallback';

const AuthorizationLoader = ({ children }) => {
  const { resolved } = useApplicationState();

  if (resolved) {
    return children;
  }

  return null;
};

const sportsbookPageActionHandler = (from, to) => {
  // Skip initial location change (no from location)
  if (!from) {
    return;
  }

  const toParams = new URLSearchParams(to.search);

  // Scroll top if we open sportsbook search popup
  if (toParams.has('q')) {
    Bus.emit('ui:scroll:content', {
      behavior: 'auto',
      scrollTo: 0,
    });

    return;
  }

  const fromParams = new URLSearchParams(from.search);
  if (fromParams.has('e')) {
    const eventId = fromParams.get('e');

    // Using small delay to wait for events
    setTimeout(() => {
      const eventNode = document.querySelector(`div[data-eid="${eventId}"]`);
      if (eventNode) {
        eventNode.scrollIntoView({ block: 'center' });
      } else {
        document.querySelector('.sport-scroll').scrollIntoView();
      }
    }, 1000);

    return;
  }

  // Check if the new path is nested relative to the old path
  const nested = decodeURIComponent(to.pathname).startsWith(decodeURIComponent(from.pathname));

  if (nested) {
    // Do not scrolling
    return;
  }

  // For event details view - currenty scrolling to the top
  const scrollOption = toParams.has('e')
    ? {
        behavior: 'auto',
        scrollTo: 0,
      }
    : {
        behavior: 'smooth',
        block: 'start',
        query: '.sport-scroll',
      };

  Bus.emit('ui:scroll:content', scrollOption);
};

export const NAVIGATION_ACTIONS_CONFIG = [
  {
    actionHandler: () => {
      Bus.emit('ui:scroll:content', {
        behavior: 'auto',
        scrollTo: 0,
      });
    },
    mask: '?e',
  },

  {
    actionHandler: sportsbookPageActionHandler,
    mask: '/sportsbook/:sport/:category?',
  },

  {
    actionHandler: sportsbookPageActionHandler,
    mask: '/live/:sport/:category?',
  },

  {
    actionHandler: from => {
      if (!from) {
        return;
      }

      Bus.emit('ui:scroll:content', {
        behavior: 'auto',
        scrollTo: 0,
      });
    },
    mask: '/*',
  },
];

const Layout = ({ children }) => {
  addBetBoosterWindowFunctions();
  useThemeBootstrap();
  useShowMessagesPopup();
  useGetUnreadMessages();
  useRedirectAfterDeposit();

  const { focused: fucusedSearch } = useSearchInputState();
  const localBetslipState = getLocalBetslipState();
  const { account, authenticated, connection } = useApplicationState();
  const accountId = account?.id ?? null;
  const pages = SiteConfigManager.getConfig('config.pages', []);

  const betslipNavConfig = React.useMemo(() => getFlyingBetslipConfig(), []);

  const gridType = React.useMemo(
    () => SiteConfigManager.getConfig('config.config.sportsbook.mobileGridViewType'),
    [],
  );
  const isSimplifiedView = gridType === 'simplified';

  const cmsBetslipSettings = React.useMemo(
    () => SiteConfigManager.getConfig('config.config.betslip', {}),
    [],
  );

  const identifyHotjarUser = React.useCallback(() => {
    if (typeof window.hj !== 'function') {
      return;
    }

    if (!authenticated) {
      window.hj('identify', null, { client_id: null });
      return;
    }

    if (accountId) {
      window.hj('identify', accountId, { client_id: accountId });
    }
  }, [accountId, authenticated]);

  useEffect(() => {
    identifyHotjarUser();
  }, [identifyHotjarUser]);

  useEffect(() => {
    Bus.on(COOKIES_ACCEPTED_EVENT, identifyHotjarUser);

    return () => {
      Bus.off(COOKIES_ACCEPTED_EVENT, identifyHotjarUser);
    };
  }, [identifyHotjarUser]);

  useActionOnNavigation(NAVIGATION_ACTIONS_CONFIG);

  const layoutClassName = cx('layout', {
    disconnected: !connection.connected,
    'simplified-view': isSimplifiedView,
  });

  const wrapperClassName = cx('layout-wrapper', {
    'search-enabled': fucusedSearch,
  });

  if (pages.length === 0) {
    printMissingConfigError('config.pages');
  }

  return (
    <Fuse fallback={LayoutErrorFallback}>
      <GlobalBetslipProvider>
        <DetectionProvider>
          <SmarticoProvider>
            <BetslipProvider settings={cmsBetslipSettings}>
              <CustomStylesPortal />
              <DefaultLayout
                AuthorizationLoader={AuthorizationLoader}
                betslipNavConfig={betslipNavConfig}
                isSimplifiedView={isSimplifiedView}
                layoutClassName={layoutClassName}
                localBetslipState={localBetslipState}
                wrapperClassName={wrapperClassName}
              >
                {children}
                <Footer />
              </DefaultLayout>
            </BetslipProvider>
          </SmarticoProvider>
        </DetectionProvider>
      </GlobalBetslipProvider>
    </Fuse>
  );
};

Layout.propTypes = {
  children: PropTypes.node.isRequired,
};

export default Layout;
